

'use client';

import * as React from 'react';
import { WhiteLogo } from '@/components/white-logo';
import { ArrowRight, Bot, BarChart2, Zap } from 'lucide-react';
import type { Locale } from '@/i18n-config';
import { Button } from '../ui/button';
import Link from 'next/link';
import Image from 'next/image';
import { LanguageSwitcher } from '../layout/language-switcher';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import type { StripeProduct } from '@/lib/data-loader';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Spinner } from '../ui/spinner';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/navigation';
import { ThemeToggle } from '../layout/theme-toggle';
import { DynamicLogo } from '../dynamic-logo';
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"
import Autoplay from "embla-carousel-autoplay"


const FeatureCard = ({ icon: Icon, title, description }: { icon: React.ElementType, title: string, description: string }) => (
    <div className="bg-card/50 backdrop-blur-sm p-8 rounded-2xl border border-white/10 flex flex-col items-center text-center transform transition-transform duration-300 hover:scale-105 hover:border-white/20">
        <div className="bg-primary/10 text-primary p-4 rounded-full mb-6 border border-white/10">
            <Icon className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold mb-4">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
    </div>
);

interface LandingClientProps {
    lang: Locale;
    dict: any;
    products: StripeProduct[];
}

export function LandingClient({ lang, dict, products }: LandingClientProps) {
  const router = useRouter();
  const videoPath = "/videos/hero-video-1.mp4";
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { toast } = useToast();
  
  const p = dict.pricing;

  const plugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false })
  )
  
  const handleSubscribe = (priceId: string | undefined) => {
    if (!priceId) {
        toast({
            variant: 'destructive',
            title: 'Configuration Error',
            description: 'The price ID for this plan is not set. Please contact support.',
        });
        return;
    }
    // For a user who is not logged in, we always redirect to register first.
    // The subscription flow will pick up after registration.
    localStorage.setItem('selectedPriceId', priceId);
    router.push(`/${lang}/register`);
  };
  
  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const getIntervalName = (interval: string | null, count: number | null) => {
      if (!interval || !count) return '';
      // Use translations for periods
      if (count === 1) return `${p.periods.per} ${p.periods[interval] || interval}`;
      return `${p.periods.per} ${count} ${p.periods[interval + 's'] || interval + 's'}`;
  }

  const getPlanTitle = (interval: string | null, count: number | null) => {
    if (interval === 'month' && count === 1) return p.plans.monthly;
    if (interval === 'month' && count === 3) return p.plans.quarterly;
    if (interval === 'month' && count === 6) return p.plans.semiannual;
    if (interval === 'year' && count === 1) return p.plans.yearly;
    return interval; // Fallback
  }

  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-20 items-center justify-between">
                <Link href="#" className="flex items-center gap-3">
                    <DynamicLogo className="w-8 h-8" />
                    <span className="text-xl font-bold">DiveBalanze</span>
                </Link>
                <nav className="flex items-center gap-4">
                    <ThemeToggle />
                    <LanguageSwitcher />
                    <Link href={`/${lang}/login`}>
                        <Button variant="outline">
                            {dict.header.login}
                        </Button>
                    </Link>
                </nav>
            </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center text-center overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-full z-0">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    loading="lazy"
                    className="absolute top-0 left-0 w-full h-full object-cover blur-sm"
                    src={videoPath}
                >
                  Your browser does not support the video tag.
                </video>
                 {/* This overlay handles the cross-fade animation */}
                <div className="absolute inset-0 bg-background/80" />
            </div>
            
            {/* Content */}
             <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight"
                    dangerouslySetInnerHTML={{ __html: dict.hero.title }}
                />
                <p className="max-w-2xl mx-auto text-lg md:text-xl text-muted-foreground mb-10">
                    {dict.hero.subtitle}
                </p>
                <div className="flex justify-center gap-4">
                    <Link href={`/${lang}/register`}>
                        <Button size="lg">
                            {dict.hero.cta} <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-card/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold">{dict.features.title}</h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                {dict.features.subtitle}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <FeatureCard 
                icon={Bot}
                title={dict.features.card1.title}
                description={dict.features.card1.description}
              />
              <FeatureCard 
                icon={BarChart2}
                title={dict.features.card2.title}
                description={dict.features.card2.description}
              />
              <FeatureCard 
                icon={Zap}
                title={dict.features.card3.title}
                description={dict.features.card3.description}
              />
            </div>
          </div>
        </section>

        {/* Image Showcase */}
        <section className="py-20">
             <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-card/50 p-4 rounded-2xl border">
                    <Carousel
                        plugins={[plugin.current]}
                        className="w-full"
                        onMouseEnter={plugin.current.stop}
                        onMouseLeave={plugin.current.reset}
                        opts={{ loop: true }}
                    >
                        <CarouselContent>
                            <CarouselItem>
                                <Image 
                                    src="/Images/DiveBalanze_imagen_1.png"
                                    alt="DiveBalanze Showcase Image 1"
                                    width={1200}
                                    height={600}
                                    className="rounded-lg shadow-2xl aspect-video object-cover mx-auto"
                                />
                            </CarouselItem>
                            <CarouselItem>
                                <Image 
                                    src="/Images/DiveBalanze_imagen_2.png"
                                    alt="DiveBalanze Showcase Image 2"
                                    width={1200}
                                    height={600}
                                    className="rounded-lg shadow-2xl aspect-video object-cover mx-auto"
                                />
                            </CarouselItem>
                             <CarouselItem>
                                <Image 
                                    src="/Images/DiveBalanze_imagen_3.png"
                                    alt="DiveBalanze Showcase Image 3"
                                    width={1200}
                                    height={600}
                                    className="rounded-lg shadow-2xl aspect-video object-cover mx-auto"
                                />
                            </CarouselItem>
                        </CarouselContent>
                    </Carousel>
                </div>
             </div>
        </section>
        
        {/* Pricing Section */}
        <section className="py-20 bg-card/10">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold">{p.title}</h2>
                    <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                        {p.subtitle}
                    </p>
                </div>
                {products.length > 0 ? (
                    products.map(product => (
                      <div key={product.id} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
                        {product.prices.sort((a,b) => (a.amount || 0) - (b.amount || 0)).map(price => {
                           const isBestValue = price.interval === 'year';
                           const discountedPrice = price.amount * 0.5;
                           return (
                               <Card key={price.id} className={cn("relative bg-card/50 backdrop-blur-sm flex flex-col", isBestValue ? 'border-2 border-primary' : 'border')}>
                                  {isBestValue && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">{p.bestValueBadge}</Badge>}
                                  <CardHeader className="text-center">
                                      <CardTitle className="text-2xl capitalize">{getPlanTitle(price.interval, price.interval_count)}</CardTitle>
                                      <CardDescription>{getIntervalName(price.interval, price.interval_count)}</CardDescription>
                                      <div className="mt-4">
                                        <p className="text-xl font-medium text-muted-foreground line-through">
                                            {formatPrice(price.amount, price.currency)}
                                        </p>
                                        <p className="text-4xl font-bold">
                                            {formatPrice(discountedPrice, price.currency)}
                                        </p>
                                         <p className="text-xs text-muted-foreground">{p.firstYear}</p>
                                      </div>
                                  </CardHeader>
                                  <CardFooter className="mt-auto">
                                      <Button 
                                          className="w-full"
                                          onClick={() => handleSubscribe(price?.id)}
                                          disabled={!!isLoading}
                                      >
                                          {isLoading === price?.id ? <Spinner /> : p.ctaButton}
                                      </Button>
                                  </CardFooter>
                              </Card>
                           )
                        })}
                      </div>
                    ))
                ) : (
                    <div className="text-center text-muted-foreground">
                        <p>{p.noPlansAvailable}</p>
                    </div>
                )}
                 <div className="text-center mt-8">
                    <p className="text-xs text-muted-foreground">{p.tax_disclaimer}</p>
                </div>
            </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center text-muted-foreground">
           <div className="flex justify-center gap-6 mb-4">
              <Link href={`/${lang}/terms-of-service`} className="text-sm hover:text-foreground transition-colors">{dict.footer.terms}</Link>
              <Link href={`/${lang}/privacy-policy`} className="text-sm hover:text-foreground transition-colors">{dict.footer.privacy}</Link>
          </div>
          <p className="text-xs">{dict.footer.copyright.replace('{year}', new Date().getFullYear().toString())}</p>
        </div>
      </footer>
    </div>
  );
}
